import { useState, useEffect, useCallback } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { ThemeProvider } from "next-themes";
import { Toaster } from "@/components/ui/sonner";
import Header from "@/components/Header";
import HeroSection from "@/components/HeroSection";
import HowItWorks from "@/components/HowItWorks";
import WhyDifferent from "@/components/WhyDifferent";
import UpcomingEvents from "@/components/UpcomingEvents";
import Pricing from "@/components/Pricing";
import FAQ from "@/components/FAQ";
import ForVenues from "@/components/ForVenues";
import Footer from "@/components/Footer";
import PostRegistration from "@/components/PostRegistration";
import BookingModal from "@/components/BookingModal";
import WaitlistModal from "@/components/WaitlistModal";
import PartnerModal from "@/components/PartnerModal";
import TermsPage from "@/pages/TermsPage";
import PrivacyPage from "@/pages/PrivacyPage";
import RefundPolicyPage from "@/pages/RefundPolicyPage";
import CodeOfConductPage from "@/pages/CodeOfConductPage";
import AdminLogin from "@/pages/AdminLogin";
import AdminLayout from "@/pages/AdminLayout";
import AdminDashboard from "@/pages/AdminDashboard";
import AdminEvents from "@/pages/AdminEvents";
import AdminBookings from "@/pages/AdminBookings";
import AdminWaitlist from "@/pages/AdminWaitlist";
import AdminWhatsAppLogs from "@/pages/AdminWhatsAppLogs";
import AdminManualSend from "@/pages/AdminManualSend";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

function ScrollToTop() {
  const { pathname } = useLocation();
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);
  return null;
}

function HomePage({ events, fetchEvents }) {
  const [bookingOpen, setBookingOpen] = useState(false);
  const [waitlistOpen, setWaitlistOpen] = useState(false);
  const [partnerOpen, setPartnerOpen] = useState(false);
  const [selectedEvent, setSelectedEvent] = useState(null);

  const handleBookSpot = (event = null) => {
    setSelectedEvent(event);
    setBookingOpen(true);
  };

  const handleJoinWaitlist = () => {
    setWaitlistOpen(true);
  };

  const handlePartnerWithUs = () => {
    setPartnerOpen(true);
  };

  return (
    <div className="min-h-screen bg-background text-foreground font-body" data-testid="app-root">
      <Header
        onBookSpot={() => handleBookSpot()}
        onJoinWaitlist={handleJoinWaitlist}
      />
      <main>
        <HeroSection
          onBookSpot={() => handleBookSpot()}
          onJoinWaitlist={handleJoinWaitlist}
          events={events}
        />
        <HowItWorks />
        <WhyDifferent />
        <UpcomingEvents
          events={events}
          onReserve={handleBookSpot}
        />
        <Pricing onBookSpot={() => handleBookSpot()} />
        <PostRegistration />
        <FAQ />
        <ForVenues onPartnerWithUs={handlePartnerWithUs} />
      </main>
      <Footer />

      <BookingModal
        open={bookingOpen}
        onOpenChange={setBookingOpen}
        event={selectedEvent}
        events={events}
        onSuccess={fetchEvents}
      />
      <WaitlistModal
        open={waitlistOpen}
        onOpenChange={setWaitlistOpen}
        events={events}
        event={selectedEvent}
        onSuccess={fetchEvents}
      />
      <PartnerModal
        open={partnerOpen}
        onOpenChange={setPartnerOpen}
      />
    </div>
  );
}

function App() {
  const [events, setEvents] = useState([]);

  const fetchEvents = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/events`);
      setEvents(res.data);
    } catch (e) {
      console.error("Failed to fetch events", e);
    }
  }, []);

  useEffect(() => {
    fetchEvents();
  }, [fetchEvents]);

  return (
    <ThemeProvider attribute="class" defaultTheme="light" enableSystem={false}>
      <BrowserRouter>
        <ScrollToTop />
        <Routes>
          <Route path="/" element={<HomePage events={events} fetchEvents={fetchEvents} />} />
          <Route path="/terms" element={<TermsPage />} />
          <Route path="/privacy" element={<PrivacyPage />} />
          <Route path="/refund-policy" element={<RefundPolicyPage />} />
          <Route path="/code-of-conduct" element={<CodeOfConductPage />} />
          <Route path="/admin/login" element={<AdminLogin />} />
          <Route path="/admin" element={<AdminLayout />}>
            <Route index element={<AdminDashboard />} />
            <Route path="events" element={<AdminEvents />} />
            <Route path="bookings" element={<AdminBookings />} />
            <Route path="waitlist" element={<AdminWaitlist />} />
            <Route path="whatsapp-logs" element={<AdminWhatsAppLogs />} />
            <Route path="manual-send" element={<AdminManualSend />} />
          </Route>
        </Routes>
        <Toaster position="bottom-right" />
      </BrowserRouter>
    </ThemeProvider>
  );
}

export default App;
